import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, Trash2, Pencil, Star, ListChecks, Hash, ToggleLeft, MessageSquare,
  ChevronUp, ChevronDown, Save, FileText, Sparkles, Link2, Lock, Eye,
} from "lucide-react";
import { toast } from "sonner";

const QUESTION_TYPES = [
  { value: "rating5", label: "Avaliação (Ótimo→Péssimo)", icon: Star, color: "text-amber-600" },
  { value: "nps10", label: "Escala 0–10 (NPS)", icon: Hash, color: "text-emerald-600" },
  { value: "yesno", label: "Sim / Não", icon: ToggleLeft, color: "text-sky-600" },
  { value: "multiselect", label: "Múltipla escolha", icon: ListChecks, color: "text-violet-600" },
  { value: "text", label: "Texto livre", icon: MessageSquare, color: "text-rose-600" },
];

const QTYPE_MAP = Object.fromEntries(QUESTION_TYPES.map((q) => [q.value, q]));

function newQuestion(type = "rating5") {
  const base = {
    label: "",
    type,
    required: true,
    options: [],
    nps_dimension: null,
    max_length: null,
    placeholder: null,
    help_text: null,
  };
  if (type === "nps10") base.nps_dimension = "teacher";
  if (type === "text") base.max_length = 500;
  if (type === "multiselect") base.options = [{ label: "Opção 1" }, { label: "Opção 2" }];
  return base;
}

function newSection() {
  return {
    title: "Nova seção",
    description: "",
    questions: [newQuestion("rating5")],
  };
}

function QuestionEditor({ q, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const typeInfo = QTYPE_MAP[q.type] || QTYPE_MAP.rating5;
  const Icon = typeInfo.icon;

  return (
    <Card className="rounded-lg border-2 border-dashed border-[hsl(var(--border))]">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <Icon className={`w-4 h-4 shrink-0 ${typeInfo.color}`} />
            <Select value={q.type} onValueChange={(v) => onChange({ ...newQuestion(v), label: q.label, required: q.required })}>
              <SelectTrigger className="h-7 text-xs w-[180px] flex-shrink-0" data-testid={`q-type`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUESTION_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value} className="text-xs">{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Badge variant="outline" className="text-[10px] shrink-0">{q.type}</Badge>
          </div>
          <div className="flex items-center gap-0.5">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onMoveUp} disabled={isFirst} title="Mover para cima">
              <ChevronUp className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onMoveDown} disabled={isLast} title="Mover para baixo">
              <ChevronDown className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600 hover:text-rose-700" onClick={onRemove} title="Remover">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        <Input
          placeholder="Pergunta (ex: Como você avalia o professor?)"
          value={q.label}
          onChange={(e) => onChange({ ...q, label: e.target.value })}
          className="text-sm"
          data-testid="q-label"
        />

        <div className="flex items-center gap-3 text-xs flex-wrap">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <Checkbox checked={q.required} onCheckedChange={(v) => onChange({ ...q, required: !!v })} />
            <span>Obrigatória</span>
          </label>
          {q.type === "nps10" && (
            <div className="flex items-center gap-1.5">
              <Label className="text-xs">Dimensão NPS:</Label>
              <Select value={q.nps_dimension || ""} onValueChange={(v) => onChange({ ...q, nps_dimension: v || null })}>
                <SelectTrigger className="h-6 text-xs w-[140px]" data-testid="q-nps-dim">
                  <SelectValue placeholder="Nenhuma" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="teacher" className="text-xs">Professor</SelectItem>
                  <SelectItem value="organization" className="text-xs">Organização</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {q.type === "text" && (
            <div className="flex items-center gap-1.5">
              <Label className="text-xs">Máx. caracteres:</Label>
              <Input
                type="number"
                value={q.max_length ?? 500}
                onChange={(e) => onChange({ ...q, max_length: Number(e.target.value) || 500 })}
                className="h-6 text-xs w-[80px]"
              />
            </div>
          )}
        </div>

        {q.type === "multiselect" && (
          <div className="border rounded-md p-2 bg-[hsl(var(--secondary))]/30">
            <p className="text-[11px] font-medium mb-1.5">Opções de múltipla escolha:</p>
            <div className="space-y-1">
              {(q.options || []).map((opt, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <Input
                    placeholder={`Opção ${idx + 1}`}
                    value={opt.label}
                    onChange={(e) => {
                      const newOpts = [...q.options];
                      newOpts[idx] = { ...opt, label: e.target.value };
                      onChange({ ...q, options: newOpts });
                    }}
                    className="h-7 text-xs"
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-rose-600"
                    onClick={() => onChange({ ...q, options: q.options.filter((_, i) => i !== idx) })}
                    disabled={q.options.length <= 1}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => onChange({ ...q, options: [...(q.options || []), { label: `Opção ${(q.options?.length || 0) + 1}` }] })}
              >
                <Plus className="w-3 h-3 mr-1" /> Adicionar opção
              </Button>
            </div>
          </div>
        )}

        {q.type === "rating5" && (
          <p className="text-[10px] text-muted-foreground italic">
            ⭐ Resposta com 5 níveis fixos: Ótimo, Bom, Regular, Ruim, Péssimo
          </p>
        )}
        {q.type === "yesno" && (
          <p className="text-[10px] text-muted-foreground italic">
            ✓ Resposta: Sim ou Não
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function SectionEditor({ section, sIdx, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const updateQuestion = (qIdx, q) => {
    const qs = [...section.questions];
    qs[qIdx] = q;
    onChange({ ...section, questions: qs });
  };
  const removeQuestion = (qIdx) => {
    onChange({ ...section, questions: section.questions.filter((_, i) => i !== qIdx) });
  };
  const moveQ = (qIdx, dir) => {
    const qs = [...section.questions];
    const ni = qIdx + dir;
    if (ni < 0 || ni >= qs.length) return;
    [qs[qIdx], qs[ni]] = [qs[ni], qs[qIdx]];
    onChange({ ...section, questions: qs });
  };

  return (
    <Card className="rounded-xl">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <Badge variant="secondary">Seção {sIdx + 1}</Badge>
          </div>
          <div className="flex items-center gap-0.5">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onMoveUp} disabled={isFirst}>
              <ChevronUp className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onMoveDown} disabled={isLast}>
              <ChevronDown className="w-3.5 h-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600" onClick={onRemove}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        <Input
          value={section.title}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          placeholder="Título da seção"
          className="font-display font-semibold text-base mt-1"
          data-testid={`section-title-${sIdx}`}
        />
        <Textarea
          rows={1}
          value={section.description || ""}
          onChange={(e) => onChange({ ...section, description: e.target.value })}
          placeholder="Descrição opcional da seção"
          className="text-xs mt-1"
        />
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {section.questions.map((q, qIdx) => (
          <QuestionEditor
            key={qIdx}
            q={q}
            onChange={(nq) => updateQuestion(qIdx, nq)}
            onRemove={() => removeQuestion(qIdx)}
            onMoveUp={() => moveQ(qIdx, -1)}
            onMoveDown={() => moveQ(qIdx, 1)}
            isFirst={qIdx === 0}
            isLast={qIdx === section.questions.length - 1}
          />
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChange({ ...section, questions: [...section.questions, newQuestion("rating5")] })}
          className="w-full"
          data-testid={`add-question-${sIdx}`}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar pergunta
        </Button>
      </CardContent>
    </Card>
  );
}

function TemplateForm({ template, onSave, onCancel, courses }) {
  const [form, setForm] = useState(() => ({
    name: template?.name || "",
    description: template?.description || "",
    scope: template?.scope || "course",
    course_ids: template?.course_ids || [],
    is_active: template?.is_active ?? true,
    sections: template?.sections?.length ? template.sections : [newSection()],
  }));
  const [saving, setSaving] = useState(false);
  const isLocked = !!template?.is_locked;

  const updateSection = (idx, s) => {
    const ss = [...form.sections];
    ss[idx] = s;
    setForm((f) => ({ ...f, sections: ss }));
  };
  const removeSection = (idx) => {
    setForm((f) => ({ ...f, sections: f.sections.filter((_, i) => i !== idx) }));
  };
  const moveSection = (idx, dir) => {
    const ss = [...form.sections];
    const ni = idx + dir;
    if (ni < 0 || ni >= ss.length) return;
    [ss[idx], ss[ni]] = [ss[ni], ss[idx]];
    setForm((f) => ({ ...f, sections: ss }));
  };

  const submit = async () => {
    if (!form.name.trim() || form.name.length < 2) {
      toast.error("Informe um nome para o template");
      return;
    }
    if (form.sections.length === 0) {
      toast.error("Adicione pelo menos uma seção");
      return;
    }
    // Valida cada pergunta
    for (const sec of form.sections) {
      for (const q of sec.questions) {
        if (!q.label.trim()) {
          toast.error(`Há perguntas sem texto na seção "${sec.title}"`);
          return;
        }
        if (q.type === "multiselect" && (!q.options || q.options.length < 1)) {
          toast.error(`Perguntas de múltipla escolha precisam de opções (em "${q.label}")`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Limpa course_ids se for default
      if (payload.scope === "default") payload.course_ids = [];
      if (template?.id) {
        await api.put(`/admin/survey-templates/${template.id}`, payload);
        toast.success("Template atualizado");
      } else {
        await api.post("/admin/survey-templates", payload);
        toast.success("Template criado");
      }
      onSave?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const totalQuestions = form.sections.reduce((acc, s) => acc + s.questions.length, 0);

  return (
    <div className="space-y-4">
      {isLocked && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-3 text-xs text-amber-900 flex items-start gap-2">
            <Lock className="w-4 h-4 shrink-0 mt-0.5" />
            <p>Este é o template <strong>padrão protegido</strong> do CEAP. Você pode editar perguntas e seções, mas não pode mudar o escopo nem deletar.</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Nome do template *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ex: Pesquisa para cursos com dois professores"
                data-testid="template-name"
              />
            </div>
            <div>
              <Label>Escopo</Label>
              <Select
                value={form.scope}
                onValueChange={(v) => setForm((f) => ({ ...f, scope: v }))}
                disabled={isLocked}
              >
                <SelectTrigger data-testid="template-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Padrão (todos os cursos sem template específico)</SelectItem>
                  <SelectItem value="course">Cursos específicos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Descrição (opcional)</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Descrição interna deste template"
            />
          </div>
          {form.scope === "course" && (
            <div>
              <Label>Cursos vinculados</Label>
              <div className="border rounded-md p-2 max-h-40 overflow-y-auto bg-[hsl(var(--secondary))]/30 space-y-1">
                {(courses || []).map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-xs py-1 cursor-pointer hover:bg-white/60 rounded px-1">
                    <Checkbox
                      checked={form.course_ids.includes(c.id)}
                      onCheckedChange={(v) => {
                        if (v) setForm((f) => ({ ...f, course_ids: [...f.course_ids, c.id] }));
                        else setForm((f) => ({ ...f, course_ids: f.course_ids.filter((x) => x !== c.id) }));
                      }}
                    />
                    <span className="flex-1 truncate">{c.title}</span>
                    <Badge variant="outline" className="text-[9px]">{c.cidade}/{c.estado}</Badge>
                  </label>
                ))}
                {(!courses || courses.length === 0) && (
                  <p className="text-xs text-muted-foreground p-2 text-center">Nenhum curso disponível</p>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Cursos vinculados a este template não usarão o padrão. Um curso pode ter apenas um template específico ativo.
              </p>
            </div>
          )}
          <div className="flex items-center gap-2 pt-2">
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
            <Label className="text-sm cursor-pointer">Template ativo (aparece para os alunos)</Label>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-[hsl(var(--primary))]">
          Seções ({form.sections.length}) · {totalQuestions} pergunta{totalQuestions !== 1 ? "s" : ""}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setForm((f) => ({ ...f, sections: [...f.sections, newSection()] }))}
          data-testid="add-section"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Nova seção
        </Button>
      </div>

      {form.sections.map((s, idx) => (
        <SectionEditor
          key={idx}
          section={s}
          sIdx={idx}
          onChange={(ns) => updateSection(idx, ns)}
          onRemove={() => removeSection(idx)}
          onMoveUp={() => moveSection(idx, -1)}
          onMoveDown={() => moveSection(idx, 1)}
          isFirst={idx === 0}
          isLast={idx === form.sections.length - 1}
        />
      ))}

      <div className="flex gap-2 pt-2 sticky bottom-0 bg-white pt-3 pb-1 border-t -mx-6 px-6">
        <Button variant="outline" onClick={onCancel} className="flex-1">Cancelar</Button>
        <Button onClick={submit} disabled={saving} className="flex-1" data-testid="save-template">
          <Save className="w-4 h-4 mr-1.5" />
          {saving ? "Salvando…" : (template?.id ? "Atualizar template" : "Criar template")}
        </Button>
      </div>
    </div>
  );
}

export default function AdminModeloPesquisa() {
  const [templates, setTemplates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} para novo | template existente
  const [previewing, setPreviewing] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/survey-templates"),
      api.get("/courses").catch(() => ({ data: [] })),
    ])
      .then(([r1, r2]) => {
        setTemplates(r1.data || []);
        setCourses(r2.data || []);
      })
      .catch(() => toast.error("Erro ao carregar templates"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setAsDefault = async (t) => {
    if (!window.confirm(`Promover "${t.name}" para template padrão de TODOS os cursos sem template específico?`)) return;
    try {
      await api.post(`/admin/survey-templates/${t.id}/set-default`);
      toast.success("Template definido como padrão");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const remove = async (t) => {
    if (t.is_locked) {
      toast.error("Este template é protegido e não pode ser deletado");
      return;
    }
    if (!window.confirm(`Excluir o template "${t.name}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/survey-templates/${t.id}`);
      toast.success("Template excluído");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  // Mapa de courseId → templateName para indicar vinculação
  const courseTplMap = useMemo(() => {
    const m = {};
    templates.forEach((t) => {
      if (t.scope === "course") {
        (t.course_ids || []).forEach((cid) => { m[cid] = t.name; });
      }
    });
    return m;
  }, [templates]);

  return (
    <AdminLayout title="Modelo de Pesquisa">
      <div className="space-y-4 max-w-5xl">
        <Card className="bg-[hsl(var(--secondary))]/40 border-0">
          <CardContent className="p-4 flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-[hsl(var(--accent))] mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium">Configure as pesquisas de satisfação aplicadas aos alunos</p>
              <p className="text-xs text-muted-foreground mt-1">
                Você pode criar um template <strong>padrão</strong> aplicado a todos os cursos, ou templates <strong>específicos</strong> para cursos com necessidades diferentes (ex: cursos com mais de um professor).
                As perguntas marcadas com dimensão NPS (professor / organização) alimentam automaticamente o dashboard.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-between">
          <h2 className="font-display font-semibold text-base text-[hsl(var(--primary))]">Templates cadastrados ({templates.length})</h2>
          <Button onClick={() => setEditing({})} data-testid="new-template-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Novo template
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Carregando…</p>
        ) : templates.length === 0 ? (
          <Card className="rounded-xl">
            <CardContent className="p-8 text-center text-sm text-muted-foreground">
              Nenhum template ainda. Clique em "Novo template" para criar o primeiro.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {templates.map((t) => {
              const totalQuestions = (t.sections || []).reduce((acc, s) => acc + (s.questions?.length || 0), 0);
              return (
                <Card key={t.id} className="rounded-xl" data-testid={`template-${t.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-display font-semibold text-sm text-[hsl(var(--primary))]">{t.name}</h3>
                          {t.scope === "default" && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Padrão</Badge>}
                          {t.is_locked && <Badge variant="outline" className="text-[10px]"><Lock className="w-2.5 h-2.5 mr-1" /> Protegido</Badge>}
                          {!t.is_active && <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">Inativo</Badge>}
                        </div>
                        {t.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.description}</p>}
                        <div className="flex flex-wrap gap-1.5 mt-2 text-[10px]">
                          <Badge variant="outline">{t.sections?.length || 0} seções</Badge>
                          <Badge variant="outline">{totalQuestions} perguntas</Badge>
                          {t.scope === "course" && (
                            <Badge variant="outline" className="bg-violet-50 text-violet-800 border-violet-200">
                              <Link2 className="w-2.5 h-2.5 mr-1" />
                              {t.course_ids?.length || 0} curso{(t.course_ids?.length || 0) !== 1 ? "s" : ""}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button size="sm" variant="ghost" onClick={() => setPreviewing(t)} data-testid={`preview-${t.id}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> Ver
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(t)} data-testid={`edit-${t.id}`}>
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                        </Button>
                        {t.scope !== "default" && !t.is_locked && (
                          <Button size="sm" variant="ghost" onClick={() => setAsDefault(t)} title="Promover a padrão">
                            <Sparkles className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {!t.is_locked && (
                          <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => remove(t)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Mapa de cursos com templates específicos */}
        {Object.keys(courseTplMap).length > 0 && (
          <Card className="rounded-xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-display flex items-center gap-1.5"><Link2 className="w-4 h-4" /> Cursos com template específico</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {Object.entries(courseTplMap).map(([cid, tname]) => {
                const c = courses.find((x) => x.id === cid);
                if (!c) return null;
                return (
                  <div key={cid} className="flex items-center justify-between text-xs py-1 border-b border-[hsl(var(--border))] last:border-0">
                    <span className="truncate flex-1">{c.title}</span>
                    <Badge variant="outline" className="text-[10px] ml-2">{tname}</Badge>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal de edição/criação */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {editing?.id ? "Editar template" : "Novo template de pesquisa"}
            </DialogTitle>
            <DialogDescription>
              Configure seções e perguntas. As respostas dos alunos serão validadas conforme as opções definidas aqui.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <TemplateForm
              template={editing.id ? editing : null}
              courses={courses}
              onSave={() => { setEditing(null); load(); }}
              onCancel={() => setEditing(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de preview */}
      <Dialog open={!!previewing} onOpenChange={(o) => !o && setPreviewing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Preview: {previewing?.name}</DialogTitle>
            <DialogDescription>Esta é a visualização exata que o aluno verá ao responder.</DialogDescription>
          </DialogHeader>
          {previewing && (
            <div className="space-y-4">
              {previewing.sections.map((sec, sIdx) => (
                <Card key={sIdx} className="rounded-xl">
                  <CardContent className="p-4 space-y-3">
                    <div>
                      <h3 className="font-display font-semibold text-base text-[hsl(var(--primary))]">{sec.title}</h3>
                      {sec.description && <p className="text-xs text-muted-foreground">{sec.description}</p>}
                    </div>
                    {sec.questions.map((q, qIdx) => (
                      <div key={qIdx} className="p-3 rounded-lg bg-[hsl(var(--secondary))]/30">
                        <p className="text-sm font-medium">
                          {q.label}
                          {q.required && <span className="text-rose-500 ml-0.5">*</span>}
                        </p>
                        <Badge variant="outline" className="text-[10px] mt-1">{q.type}</Badge>
                        {q.type === "multiselect" && (
                          <ul className="mt-1.5 ml-3 text-xs text-muted-foreground list-disc">
                            {q.options.map((opt, i) => <li key={i}>{opt.label}</li>)}
                          </ul>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
